/**
 * @fileoverview Module for `src/campaigns/CallToAction.js`.
 */

import axios from 'axios';
import { Link } from 'react-router-dom';
import propTypes from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';

import VerifyEmailRequest from '../VerifyEmailRequest';

import { affiliateWithPartner } from '../partners/lib/affiliate';
import { isDatePast } from '../lib/dates';
import logger from '../lib/logger';
import { segmentTrack } from '../lib/segment';

/**
 * Campaign call-to-action block for authenticated and unauthenticated users.
 *
 * @param {Object} params
 * @returns {JSX.Element} CTA block.
 */
function CallToAction({ allDone, auth, district, partnerSlug }) {
  const isAuthenticated = auth.isAuthenticated();
  const isCampaignPast = isDatePast(district.mail_date) || isDatePast(district.election_date);
  const isCampaignClosed = isCampaignPast || allDone;

  const [partnerName, setPartnerName] = useState();
  const sessionPartnerSlug = sessionStorage.getItem('partner');
  // Signed-in users see the route's partner, the one the click affiliates them with.
  const disclaimerPartnerSlug = isAuthenticated ? partnerSlug : sessionPartnerSlug;
  useEffect(() => {
    /**
     * Fetches the partner's display name from the API and stores it in state for the affiliation disclaimer.
     */
    async function getPartnerName() {
      try {
        const url = `${ process.env.REACT_APP_API_URL }/v1/partners/${ disclaimerPartnerSlug }`;
        const { data: partner } = await axios.get(url);
        setPartnerName(partner.name);
      } catch (error) {
        logger.error(error);
        if (!isAuthenticated) {
          sessionStorage.removeItem('partner');
        }
      }
    }

    setPartnerName(undefined);
    if (!isCampaignClosed && disclaimerPartnerSlug) {
      getPartnerName();
    }
  }, [isAuthenticated, isCampaignClosed, disclaimerPartnerSlug]);

  /**
   * Tracks CTA click analytics for campaign conversion.
   */
  const trackCtaClick = () => {
    segmentTrack('CampaignCTA:click', {
      category: 'cta',
      campaign: district.district_id,
      isPolitical: district.tax_type === 'political',
      isSocial: district.tax_type === 'social',
    });
  };

  /**
   * Tracks the CTA click and affiliates the user with the route's partner.
   */
  const handleCtaClick = () => {
    trackCtaClick();
    affiliateWithPartner({ auth, partnerSlug });
  };

  const partnerDisclaimer = partnerName && (
    <div className='partner-affiliation-disclaimer login-widget'>
      <div className='card border-0 small text-center'>
        <div className='card-body pt-3'>
          <strong>
            Vote Forward and { partnerName } are working together to increase civic participation by sending letters to voters.
          </strong>
          { ' ' }
          { partnerName } can see the email addresses and info about the letter-writing activities of volunteers who { isAuthenticated ? 'write letters' : 'sign up' } through this page. They use this information to measure their impact. They may also follow up with you by email.
        </div>
      </div>
    </div>
  );

  return isAuthenticated
    ? (
      <div>
        { !isCampaignClosed && (
          <Fragment>
            <Link
              className='btn btn-primary btn-lg d-block'
              onClick={ handleCtaClick }
              to={ `/dashboard/${ district.district_id }` }
            >
              Write letters to voters in { district.state }
            </Link>
            { partnerDisclaimer }
          </Fragment>
        ) }

        { isCampaignClosed && (
          <Link
            className='btn btn-primary btn-lg d-block'
            to='/campaigns'
          >
            Campaign completed! Find another campaign
          </Link>
        ) }
      </div>
      )
    : (
      <div>
        { !isCampaignClosed && (
          <Fragment>
            <VerifyEmailRequest
              getStartedMessage={ false }
              hideFooter
              hideHeader
              hideMasthead
              purpose='register.user'
            />
            { partnerDisclaimer }
          </Fragment>
        ) }

        { isCampaignClosed && (
          <Link
            className='btn btn-primary btn-lg d-block'
            to='/campaigns'
          >
            Campaign completed! Find another campaign
          </Link>
        ) }
      </div>
      );
}

CallToAction.propTypes = {
  allDone: propTypes.bool,
  auth: propTypes.object,
  district: propTypes.object,
  partnerSlug: propTypes.string,
};

CallToAction.defaultProps = {
  partnerSlug: '',
};

export default CallToAction;
